import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  Modal,
  TextInput,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes, layout } from '../../../../styles/variablesCss';
import { useUser } from '../../../../context/UserContext';
import Storage from '../../../../utils/Storage';
import { BACKEND_URL } from '../../../../Config/Const';
import { useCart } from '../../../../context/CartContext';
import { useFavoritos } from '../../../../context/FavoritosContext';
import { useProducts } from '../../../../context/ProductContext';
import { useNavigation } from '@react-navigation/native';
import CambiarContra from './CambiarContra';
import DatosUsuario from './DatosUsuario';
import Envio from '../../../Checkout/Envio';
import HistorialDeCompra from './HistorialDeCompra';
import QueMasCompras from './QueMasCompras';
 import VerificationCodeModal from './VerificationCodeModal';

// Detección de dispositivo móvil simple para usar en estilos
const isWeb = Platform.OS === 'web';
const detectMobile = () => {
  if (isWeb && typeof window !== 'undefined') {
    return window.innerWidth < 768;
  }
  return true;
};

// Función para obtener el ancho de la ventana
const getWindowWidth = () => {
  if (isWeb && typeof window !== 'undefined') {
    return window.innerWidth;
  }
  return 768; // Default para móvil nativo
};

const Perfil = () => {
  const navigationHook = useNavigation();
  const { cargarFavoritos, favoritos, favoritosFaltantes } = useFavoritos();
  const { logout, codUsu, updateUser, nombreUsuario } = useUser();
  const { cargarProductosCarrito } = useCart();
  const { setProductos, setSearchText, setDepartmentId, setCategoryId, setSubcategoryId,
    setDepartmentName, setCategoryName, setSubcategoryName } = useProducts();

  // Handlers usados por event listeners (actualmente no realizan acciones)
  const handleResize = () => { };
  const handleURLChange = () => { };

  // Estados para edición de perfil
  const [editMode, setEditMode] = useState(false);
  // Estado para manejar el ancho de pantalla
  const [windowWidth, setWindowWidth] = useState(getWindowWidth());
  const isSmallScreen = windowWidth < 500;
  
  // Estado para formulario de promociones (simplificado)
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [promoFeedback, setPromoFeedback] = useState(null);
  // Estado para ciudad (para enlaces de WhatsApp)
  const [ciudad, setCiudad] = useState(null);
  // Referencias a los componentes
  const datosUsuarioRef = useRef();
  const cambiarContraRef = useRef();
  const queMasComprasRef = useRef();

  // Estados de acordeón por sección (inician todos cerrados)
  const [openPerfil, setOpenPerfil] = useState(false);
  const [openDirecciones, setOpenDirecciones] = useState(false);
  const [openCambioContra, setOpenCambioContra] = useState(false);
  const [openResumenCompra, setOpenResumenCompra] = useState(false);
  const [openSalir, setOpenSalir] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);

  const [prefillContraActual, setPrefillContraActual] = useState('');

  const [showVerification, setShowVerification] = useState(false);
  const [verificationMail, setVerificationMail] = useState('');

  // Estado para controlar cuando se solicita ver favoritos
  const [shouldNavigateToFavoritos, setShouldNavigateToFavoritos] = useState(false);

  // Estado para controlar el layout responsivo
  const [isMobile, setIsMobile] = useState(detectMobile());

  // Función para manejar el toggle de acordeones (solo uno abierto a la vez, y permite cerrar el abierto)
  const handleAccordionToggle = (accordionName) => {
    // ¿El acordeón seleccionado ya está abierto?
    const isCurrentlyOpen = (
      (accordionName === 'perfil' && openPerfil) ||
      (accordionName === 'direcciones' && openDirecciones) ||
      (accordionName === 'cambioContra' && openCambioContra) ||
      (accordionName === 'resumenCompra' && openResumenCompra) ||
      (accordionName === 'salir' && openSalir)
    );

    // Cerrar todos
    setOpenPerfil(false);
    setOpenDirecciones(false);
    setOpenCambioContra(false);
    setOpenResumenCompra(false);
    setOpenSalir(false);

    // Si no estaba abierto, abrir el seleccionado. Si ya estaba, queda todo cerrado.
    if (!isCurrentlyOpen) {
      switch (accordionName) {
        case 'perfil':
          setOpenPerfil(true);
          break;
        case 'direcciones':
          setOpenDirecciones(true);
          break;
        case 'cambioContra':
          setOpenCambioContra(true);
          break;
        case 'resumenCompra':
          setOpenResumenCompra(true);
          break;
        case 'salir':
          setOpenSalir(true);
          break;
        default:
          break;
      }
    }
  };

  // Actualizar isMobile y windowWidth cuando cambia el tamaño de la ventana
  useEffect(() => {
    if (isWeb && typeof window !== 'undefined') {
      const handleResizeInternal = () => {
        setIsMobile(detectMobile());
        setWindowWidth(getWindowWidth());
      };

      window.addEventListener('resize', handleResizeInternal);
      return () => {
        window.removeEventListener('resize', handleResizeInternal);
      };
    }
  }, []);

  // Cargar ciudad desde Storage para enlaces de WhatsApp
  useEffect(() => {
    const loadCiudad = async () => {
      try {
        const storedCiudad = await Storage.getItem('ciudad');
        setCiudad(storedCiudad ? Number(storedCiudad) : null);
      } catch (e) {
        setCiudad(null);
      }
    };
    loadCiudad();

    // Escuchar cambios en la ciudad
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleStorageChange = (e) => {
        if (e?.detail?.key === 'ciudad' || e?.detail?.key === 'ciudadNom' || e?.detail?.key === 'sucursalId') {
          loadCiudad();
        }
      };

      window.addEventListener('localStorageChange', handleStorageChange);
      return () => {
        window.removeEventListener('localStorageChange', handleStorageChange);
      };
    }
  }, []);

  // Redirigir a /home si no hay codUsu (robusto: verifica ruta, localStorage y Storage util)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    // Sólo aplicar guardia en la página de perfil
    const inPerfil = window.location.pathname.startsWith('/perfil');
    if (!inPerfil) return;

    let cancelled = false;
    let inFlight = false;
    const checkAuth = async () => {
      const storedCodLS = window.localStorage ? window.localStorage.getItem('codUsu') : '';
      const storedCodUtil = await Storage.getItem('codUsu').catch(() => '');
      const hasUser = [codUsu, storedCodLS, storedCodUtil].some(v => v && String(v).trim() !== '');

      if (hasUser || cancelled) return;

      if (!inFlight) {
        inFlight = true;
        try {
          const params = new URLSearchParams(window.location.search || '');
          const mailParam = params.get('mail');
          const contParam = params.get('cont');

          if (mailParam && contParam) {
            const loginUrl = `${BACKEND_URL}/rest/usuarios/login?mail=${encodeURIComponent(mailParam)}&cont=${encodeURIComponent(contParam)}`;
            const response = await fetch(loginUrl, { method: 'POST' });
            const data = await response.json().catch(() => ({}));

            if (response.ok && data.dsResultado?.Usuario?.[0]) {
              const user = data.dsResultado.Usuario[0];
              const userData = {
                codUsu: user.codUsu?.toString(),
                nombre: user.nombre,
                mail: user.mail,
                genero: user.genero,
                fechaNac: user.fechaNac,
                cel: user.cel,
                tel: user.tel,
              };

              await updateUser(userData);

              try {
                const first = await cargarProductosCarrito();
                if (!first || first.success !== true) {
                  await new Promise(resolve => setTimeout(resolve, 1100));
                  await cargarProductosCarrito();
                }
              } catch (e) {
                
              }

              setPrefillContraActual(contParam);
              setOpenPerfil(false);
              setOpenDirecciones(false);
              setOpenCambioContra(true);
              setOpenResumenCompra(false);
              setOpenSalir(false);

              try {
                window.history.replaceState({}, '', '/perfil');
              } catch (e) {
              }
              return;
            }

            const resultRow = data?.dsResultado?.resultado?.[0];
            const mensaje = (resultRow?.mensaje || '').toString().trim().toLowerCase();
            if (resultRow && resultRow.resultado === false && mensaje === 'validar') {
              setVerificationMail(mailParam);
              setShowVerification(true);
              try {
                window.history.replaceState({}, '', `/login?mail=${encodeURIComponent(mailParam)}`);
              } catch (e) {
              }
              return;
            }
          }
        } catch (e) {
        } finally {
          inFlight = false;
        }
      }

      if (!cancelled) {
        window.location.replace('/');
      }
    };
    // Pequeño delay para evitar carrera con la hidratación del contexto
    const t = setTimeout(checkAuth, 30);
    return () => { cancelled = true; clearTimeout(t); };
  }, [codUsu, updateUser]);

  // Si estamos en web, escuchar cambios en la URL
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('popstate', handleURLChange);
      return () => {
        window.removeEventListener('popstate', handleURLChange);
      };
    }
  }, []);


  // Función para ver favoritos
  const handleVerFavoritos = async () => {
    try {
      // Marcar que queremos navegar a favoritos
      setShouldNavigateToFavoritos(true);

      // Cargar favoritos desde la API
      await cargarFavoritos();
    } catch (error) {
      console.error('Error al cargar favoritos:', error);
      setShouldNavigateToFavoritos(false);
    }
  };

  // useEffect para navegar cuando los favoritos se hayan cargado
  useEffect(() => {
    if (shouldNavigateToFavoritos && favoritos && favoritos.length >= 0) {
      // Limpiar contexto de búsqueda y categorías
      setSearchText('');
      setDepartmentId('0');
      setCategoryId('0');
      setSubcategoryId('0');
      setDepartmentName('');
      setCategoryName('');
      setSubcategoryName('');

      const disponibles = Array.isArray(favoritos) ? favoritos : [];
      const faltantes = Array.isArray(favoritosFaltantes)
        ? favoritosFaltantes.map((p) => ({
            ...p,
            faltante: true,
            precio: p.precio ?? 0,
            precioSinImp: p.precioSinImp ?? 0,
            stock: p.stock ?? 0,
            maxStock: p.maxStock ?? 0,
            minStock: p.minStock ?? 1,
            oferta: p.oferta ?? '',
            cuotasSinInt: p.cuotasSinInt ?? 0,
            fotos: p.fotos ?? (p.foto ? [{ plu: p.plu, imagen: p.foto }] : []),
          }))
        : [];

      // Pasar productos favoritos (disponibles + faltantes al final) al contexto de productos
      setProductos([...disponibles, ...faltantes]);

      // Navegar a /productos
      if (Platform.OS === 'web' && window) {
        // Usar window.location.href para hacer una redirección real
        window.location.href = '/productos?favoritos=true';
      } else if (navigationHook) {
        navigationHook.navigate('Productos', { favoritos: true });
      }

      // Reset el flag
      setShouldNavigateToFavoritos(false);
    }
  }, [favoritos, favoritosFaltantes, shouldNavigateToFavoritos]);

  // Función para cerrar sesión (usa logout del contexto)
  const handleLogout = () => {
    logout();
  };

  // Handler para sumarse a promociones
  const handlePromoSubmit = () => {
    // Links de WhatsApp según ciudad
    const whatsappLinks = {
      1: 'https://whatsapp.com/channel/0029VbCIduRLo4hhznDAXm3M', // Rafaela
      2: 'https://whatsapp.com/channel/0029VbBqZPGKQuJQHSDyJV02'  // San Francisco
    };
    
    // Usar link de ciudad 1 por defecto si no hay ciudad seleccionada
    const link = whatsappLinks[ciudad] || whatsappLinks[1];
    
    // Abrir el enlace de WhatsApp en una nueva pestaña
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  // Renderizado de las secciones principales
  const renderSeccionPerfil = () => {
    return (
      <View>
        <DatosUsuario
          ref={datosUsuarioRef}
          editMode={editMode}
          setEditMode={setEditMode}
        />
        <View style={styles.bottomDivider} />
      </View>
    );
  };

  const renderSeccionContrasena = () => {
    return (
      <View>
        <CambiarContra
          ref={cambiarContraRef}
          editMode={editMode}
          setEditMode={setEditMode}
          prefillContraActual={prefillContraActual}
        />
      </View>
    );
  };

  const renderSeccionResumenCompra = () => {
    return (
      <View>
        <HistorialDeCompra />
      </View>
    );
  };

  const AccordionHeader = ({ title, open, onToggle, right }) => (
    <TouchableOpacity
      style={[
        styles.accordionHeader,
      ]}
      onPress={onToggle}
      activeOpacity={0.8}
    >
      <Text style={styles.accordionTitle}>{title}</Text>
      <View style={styles.accordionRight}>
        {right}
        <MaterialCommunityIcons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={22}
          color={colores.texto}
        />
      </View>
      {!open && (
        <View style={[styles.accordionBottomPill, { pointerEvents: 'none' }]} />
      )}
    </TouchableOpacity>
  );

  const renderContenidoAcordeon = () => (
    <View>
      {/* Perfil */}
      <AccordionHeader title="Perfil" open={openPerfil} onToggle={() => handleAccordionToggle('perfil')} />
      {openPerfil && (
        <View style={styles.accordionBody}>
          {renderSeccionPerfil()}
        </View>
      )}
      {/* Lo que mas Compras */}
      <View style={styles.simpleRow}>
        <TouchableOpacity
          onPress={() => {
            if (queMasComprasRef.current && queMasComprasRef.current.fetchMasCompras) {
              queMasComprasRef.current.fetchMasCompras();
            }
          }}
          activeOpacity={0.7}
        >
          <Text style={styles.simpleRowTitle}>Lo que mas Compras</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.verButton}
          onPress={() => {
            if (queMasComprasRef.current && queMasComprasRef.current.fetchMasCompras) {
              queMasComprasRef.current.fetchMasCompras();
            }
          }}
        >
          <MaterialCommunityIcons name="eye" size={18} color={colores.rojo} />
          <Text style={styles.verButtonText}>Ver</Text>
        </TouchableOpacity>
      </View>
      {/* Componente oculto solo para mantener la ref disponible */}
      <View style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <QueMasCompras ref={queMasComprasRef} autoFetch={false} />
      </View>
      {/* Favoritos */}
      <View style={styles.simpleRow}>
        <TouchableOpacity onPress={handleVerFavoritos} activeOpacity={0.7}>
          <Text style={styles.simpleRowTitle}>Favoritos</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.verButton}
          onPress={handleVerFavoritos}
        >
          <MaterialCommunityIcons name="eye" size={18} color={colores.rojo} />
          <Text style={styles.verButtonText}>Ver</Text>
        </TouchableOpacity>
      </View>
      {/* Resumen de Compra */}
      <AccordionHeader title="Resumen de compras" open={openResumenCompra} onToggle={() => handleAccordionToggle('resumenCompra')} />
      {openResumenCompra && (
        <View style={styles.accordionBody}>
          {renderSeccionResumenCompra()}
        </View>
      )}
      {/* Direcciones */}
      <AccordionHeader
        title="Direcciones"
        open={openDirecciones}
        onToggle={() => handleAccordionToggle('direcciones')}
      />
      {openDirecciones && (
        <View style={styles.accordionBody}>
          <Envio />
        </View>
      )}
      {/* Cambio de contraseña */}
      <AccordionHeader title="Cambio de contraseña" open={openCambioContra} onToggle={() => handleAccordionToggle('cambioContra')} />
      {openCambioContra && (
        <View style={styles.accordionBody}>
          {renderSeccionContrasena()}
        </View>
      )}
      <View style={styles.simpleRow}>
        <TouchableOpacity onPress={() => setLogoutModalVisible(true)} activeOpacity={0.7}>
          <Text style={styles.simpleRowTitle}>Salir</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.verButton}
          onPress={() => setLogoutModalVisible(true)}
        >
          <MaterialCommunityIcons name="exit-to-app" size={18} color={colores.rojo} />
          <Text style={styles.verButtonText}>Cerrar sesión</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Estilos dinámicos basados en el estado isMobile
  const dynamicStyles = {
    header: {
      paddingVertical: 12,
      marginHorizontal: isMobile ? layout.contentPaddingMobile : layout.contentPadding,
      //paddingHorizontal: isMobile ? layout.contentPaddingMobile : layout.contentPadding,
      borderBottomWidth: 2,
      borderBottomColor: 'red',
      position: 'relative',
      zIndex: 0,
    },
    mainContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      paddingVertical: 15,
      paddingHorizontal: isMobile ? layout.contentPaddingMobile : layout.contentPadding,
      gap: 15,
      position: 'relative',
      zIndex: 0,
    },
    contenidoPrincipalRow: {
      flex: 1,
      backgroundColor: 'transparent',
      padding: 0,
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'stretch',
      gap: 12,
      position: 'relative',
      zIndex: 0,
    },
    leftColumn: {
      width: isMobile ? '100%' : '70%',
      position: 'relative',
      zIndex: 0,
    },
    rightPane: {
      width: isMobile ? '100%' : '30%',
      marginTop: isMobile ? 15 : 0,
      position: 'relative',
      zIndex: 0,
    },
    leftCard: {
      backgroundColor: colores.blanco,
      borderRadius: 8,
      padding: isMobile ? 12 : 20,
      position: 'relative',
      zIndex: 0,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
        : {
          shadowColor: colores.negro,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 3.84,
          elevation: 3,
        }
      ),
    },
  };

  return (
    <>
      <ScrollView style={styles.container}>
        {/* Saludo con icono de usuario */}
        <View style={[styles.saludoContainer, dynamicStyles.header]}>
          <View style={styles.iconoCircular}>
            <MaterialCommunityIcons name="account" size={32} color={colores.rojo} weight={0.5} />
          </View>
          <View style={styles.saludoTextoContainer}>
            <Text style={styles.saludoTextHola}>Hola</Text>
            <Text style={styles.saludoText}>{nombreUsuario || 'Usuario'}!</Text>
          </View>
        </View>

        {/* Contenedor principal con acordeones y panel derecho dentro del mismo card */}
        <View style={dynamicStyles.mainContainer}>
          <View style={dynamicStyles.contenidoPrincipalRow}>
            <View style={dynamicStyles.leftColumn}>
              <View style={dynamicStyles.leftCard}>
                {renderContenidoAcordeon()}
              </View>
            </View>
            <View style={dynamicStyles.rightPane}>
              <View style={styles.boxPromo}>
                <Text style={styles.promoTitle}>RECIBÍ LAS OFERTAS EN TU CELULAR</Text>
                <Text style={styles.promoText}>
                  Sumate a nuestras listas de difusión en Whatsapp.
                </Text>
                
                <TouchableOpacity
                  style={styles.promoPrimaryBtn}
                  onPress={handlePromoSubmit}
                >
                  <Text style={styles.promoPrimaryBtnText}>¡Me sumo!</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>

      <Modal
        animationType="fade"
        transparent={true}
        visible={logoutModalVisible}
        onRequestClose={() => setLogoutModalVisible(false)}
      >
        <View style={styles.modalOverlayCenter}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>¿Cerrar sesión?</Text>
            <Text style={styles.modalText}>¡Esperamos que vuelvas pronto!</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalBtnSecondary} onPress={() => setLogoutModalVisible(false)}>
                <Text style={styles.modalBtnSecondaryText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtnPrimary} onPress={handleLogout}>
                <Text style={styles.modalBtnPrimaryText}>Cerrar sesión</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <VerificationCodeModal
        visible={showVerification}
        mail={verificationMail}
        navigation={navigationHook}
        onClose={() => {
          setShowVerification(false);
        }}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colores.fondo,
    position: 'relative',
    zIndex: 0,
  },
  saludoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconoCircular: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: colores.rojo,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 3,
  },
  saludoTextoContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  saludoTextHola: {
    fontSize: 12,
    fontFamily: fuentes.semiBold,
    color: colores.texto,
  },
  saludoText: {
    fontSize: 14,
    fontFamily: fuentes.semiBold,
    color: colores.texto,
  },
  titulo: {
    fontSize: 24,
    fontFamily: fuentes.bold,
    color: colores.rojo,
  },
  boxPromo: {
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: 0,
    borderWidth: 0,
  },
  promoTitle: {
    color: colores.rojo,
    fontFamily: fuentes.bold,
    fontSize: 18,
    marginBottom: 8,
  },
  promoText: {
    color: colores.texto,
    fontFamily: fuentes.regular,
    fontSize: 14,
    marginBottom: 12,
  },
  promoFormRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  promoFormColumn: {
    flexDirection: 'column',
  },
  promoInputGroup: {
    flex: 1,
    
    marginRight: 10,
    marginBottom: 10,
  },
  promoInputGroupFull: {
    flex: undefined,
    width: '100%',
  },
  promoInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    padding: 10,
    backgroundColor: '#fff',
    fontSize: 14,
    fontFamily: fuentes.regular,
  },
  promoInputError: {
    borderColor: colores.rojo,
  },
  promoCheckRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
    marginBottom: 12,
  },
  promoCheckbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderRadius: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  promoCheckboxUnchecked: {
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  promoCheckboxChecked: {
    borderColor: colores.rojo,
    backgroundColor: colores.rojo,
  },
  promoCheckmark: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 12,
  },
  promoCheckLabel: {
    fontSize: 13,
    color: colores.texto,
    fontFamily: fuentes.regular,
  },
  promoLinkText: {
    color: colores.rojo,
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  promoPrimaryBtn: {
    alignSelf: 'stretch',
    marginTop: 5,
    backgroundColor: colores.rojo,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  promoPrimaryBtnText: {
    color: colores.blanco,
    fontFamily: fuentes.semiBold,
    fontSize: 14,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 8,
    position: 'relative',
    zIndex: 0,
  },
  accordionBottomPill: {
    position: 'absolute',
    left: 8,
    right: 8,
    bottom: 0,
    height: 2,
    backgroundColor: colores.rojo,
    headerHeight: 1,
    borderRadius: 8,
    zIndex: 0,
  },
  accordionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.textoPrincipal || colores.texto,
  },
  accordionRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  simpleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    marginHorizontal: 8,
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
  },
  simpleRowTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.textoPrincipal || colores.texto,
  },
  verButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  verButtonText: {
    color: colores.rojo,
    fontFamily: fuentes.semiBold,
    fontSize: 13,
  },
  accordionBody: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  mensajeInfo: {
    fontSize: 16,
    fontFamily: fuentes.regular,
    color: '#666',
    textAlign: 'center',
    paddingVertical: 30,
  },
  botonCancelar: {
    backgroundColor: colores.blanco,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 5,
    paddingHorizontal: 15,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  botonCancelarTexto: {
    color: colores.texto,
    fontSize: 16,
    fontFamily: fuentes.medium,
  },
  modalOverlayCenter: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCard: {
    width: '85%',
    maxWidth: 420,
    backgroundColor: colores.blanco,
    padding: 20,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.2,
        shadowRadius: 12,
        elevation: 10,
      }
    ),
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: fuentes.bold,
    fontWeight: 'bold',
    color: colores.rojo,
    marginBottom: 6,
    textAlign: 'center',
  },
  modalText: {
    fontSize: 14,
    fontFamily: fuentes.regular,
    color: '#666',
    textAlign: 'center',
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    marginTop: 6,
  },
  modalBtnSecondary: {
    backgroundColor: colores.blanco,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  modalBtnSecondaryText: {
    color: colores.texto,
    fontFamily: fuentes.medium,
    fontSize: 14,
  },
  modalBtnPrimary: {
    backgroundColor: colores.rojo,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  modalBtnPrimaryText: {
    color: colores.blanco,
    fontFamily: fuentes.semiBold,
    fontSize: 14,
  },
  bottomDivider: {
    width: '100%',
    height: 1,
    backgroundColor: colores.negro,
    marginVertical: 0,
  },
});

export default Perfil;
